import { yupResolver } from '@hookform/resolvers/yup';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { postV1CompaniesCompanyIdOrdersSimulateOrder } from '@treadinc/horizon-api-spec';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import AutocompleteAccountsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteAccountsTypeahead';
import AutocompleteDrivers from '~components/FormFields/CommonAutocompletes/AutocompleteDrivers';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const simulateOrderValidationSchema = yup.object({
  customer: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .notRequired(),
  sharedDriver: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .notRequired(),
  internalDriver: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .notRequired(),
});

type SimulateOrderFormData = yup.InferType<typeof simulateOrderValidationSchema>;

const SimulateOrderCTA = () => {
  const [isSimulateOrderDialogOpen, setIsSimulateOrderDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toasterStore, userStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const formMethods = useForm<SimulateOrderFormData>({
    resolver: yupResolver(simulateOrderValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      customer: null,
      sharedDriver: null,
      internalDriver: null,
    },
  });

  const { handleSubmit, reset } = formMethods;

  const handleCloseDialog = () => {
    setIsSimulateOrderDialogOpen(false);
    reset();
  };

  const handleSave = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      const { customer, sharedDriver, internalDriver } = data;

      await postV1CompaniesCompanyIdOrdersSimulateOrder({
        path: { 'company-id': companyId },
        body: {
          ...(customer && { customer_account_id: customer.value }),
          ...(sharedDriver && { shared_driver_id: sharedDriver.value }),
          ...(internalDriver && { driver_id: internalDriver.value }),
        },
      });

      toasterStore.push(
        alert(t('dispatch.dispatch_v2.simulate_order.simulating'), AlertTypes.success),
      );

      handleCloseDialog();
    } catch (error) {
      console.error(error);
      connection.handleRequestError(
        error,
        t('error_messages.simulate_order.failed_to_simulate_order'),
        [422],
      );
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      <SmallButton
        color="brandV2Black"
        startIcon={<EventRepeatIcon />}
        onClick={() => setIsSimulateOrderDialogOpen(true)}
      >
        {t('dispatch.dispatch_v2.simulate_order.title')}
      </SmallButton>

      <Dialog isOpen={isSimulateOrderDialogOpen} onClose={handleCloseDialog}>
        <DialogHeader onClose={handleCloseDialog}>
          {t('dispatch.dispatch_v2.simulate_order.title')}
        </DialogHeader>
        <DialogContent>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 300,
              color: 'text.secondary',
              whiteSpace: 'pre-line',
            }}
          >
            {t('dispatch.dispatch_v2.simulate_order.description')}
          </Typography>
          <FormProvider {...formMethods}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <AutocompleteAccountsTypeahead
                    name="customer"
                    label={t('dispatch.dispatch_v2.simulate_order.customer')}
                    companyId={companyId}
                    accountTypes={[AccountType.CUSTOMER]}
                    isRequired={false}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <AutocompleteDrivers
                    name="internalDriver"
                    label={t('dispatch.dispatch_v2.simulate_order.internal_driver')}
                    companyId={companyId}
                    shared={false}
                    isRequired={false}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <AutocompleteDrivers
                    name="sharedDriver"
                    label={t('dispatch.dispatch_v2.simulate_order.shared_driver')}
                    companyId={companyId}
                    shared={true}
                    isRequired={false}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            onClick={handleCloseDialog}
            variant="outlined"
            disabled={isLoading}
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={isLoading}
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            {t('dispatch.dispatch_v2.simulate_order.simulate')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimulateOrderCTA;
